import React, { useState, useMemo } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { userProfile, notifications } from '../mockData';
import { User, Calendar, Bell, Settings, Heart, MessageCircle, Edit, Facebook } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import MessengerSettings from '../components/MessengerSettings';

const Profile = () => {
  const { currentUser } = useAuth();
  const initial = useMemo(() => {
    if (!currentUser) return userProfile;
    return {
      name: currentUser.name || 'Người dùng',
      email: currentUser.email || '',
      avatar: currentUser.avatar || '',
      dueDate: currentUser.dueDate || new Date().toISOString().slice(0,10),
      currentWeek: parseInt(currentUser.currentWeek || 12),
      posts: typeof currentUser.posts === 'number' ? currentUser.posts : 0,
      comments: typeof currentUser.comments === 'number' ? currentUser.comments : 0,
    };
  }, [currentUser]);

  const [profile, setProfile] = useState(initial);
  const [isEditing, setIsEditing] = useState(false);

  const handleSaveProfile = () => {
    setIsEditing(false);
    toast.success('Cập nhật thông tin thành công!');
  };

  const daysUntilDue = Math.ceil((new Date(profile.dueDate) - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-pink-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
              Hồ sơ của tôi
            </span>
          </h1>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Sidebar - Profile Info */}
          <div className="space-y-6">
            <Card className="border-pink-100 shadow-lg">
              <CardContent className="p-6 text-center">
                <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-pink-200">
                  <AvatarImage src={profile.avatar} />
                  <AvatarFallback className="bg-pink-100 text-pink-600 text-3xl">
                    {profile.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{profile.name}</h2>
                <p className="text-gray-600 mb-4">{profile.email}</p>
                <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-200 text-sm px-4 py-1">
                  Tuần {profile.currentWeek}
                </Badge>
              </CardContent>
            </Card>

            <Card className="border-pink-100 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Calendar className="w-5 h-5 mr-2 text-pink-500" />
                  Thống kê
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg">
                  <span className="text-gray-600">Ngày dự sinh</span>
                  <span className="font-semibold text-gray-800">
                    {new Date(profile.dueDate).toLocaleDateString('vi-VN')}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg">
                  <span className="text-gray-600">Còn lại</span>
                  <span className="font-semibold text-pink-600">{daysUntilDue} ngày</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg">
                  <span className="text-gray-600">
                    <Heart className="w-4 h-4 inline mr-1" />
                    Bài viết
                  </span>
                  <span className="font-semibold text-gray-800">{profile.posts}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg">
                  <span className="text-gray-600">
                    <MessageCircle className="w-4 h-4 inline mr-1" />
                    Bình luận
                  </span>
                  <span className="font-semibold text-gray-800">{profile.comments}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="md:col-span-2">
            <Card className="border-pink-100 shadow-lg">
              <CardContent className="p-6">
                <Tabs defaultValue="info" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-6">
                    <TabsTrigger value="info" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                      <User className="w-4 h-4 mr-2" />
                      Thông tin
                    </TabsTrigger>
                    <TabsTrigger value="notifications" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                      <Bell className="w-4 h-4 mr-2" />
                      Thông báo
                    </TabsTrigger>
                    <TabsTrigger value="settings" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                      <Settings className="w-4 h-4 mr-2" />
                      Cài đặt
                    </TabsTrigger>
                  </TabsList>

                  {/* Personal Info Tab */}
                  <TabsContent value="info" className="space-y-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-800">Thông tin cá nhân</h3>
                      {!isEditing ? (
                        <Button
                          onClick={() => setIsEditing(true)}
                          variant="outline"
                          className="border-pink-300 text-pink-600 hover:bg-pink-50"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Chỉnh sửa
                        </Button>
                      ) : (
                        <div className="flex space-x-2">
                          <Button variant="outline" onClick={() => setIsEditing(false)}>
                            Hủy
                          </Button>
                          <Button
                            onClick={handleSaveProfile}
                            className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white"
                          >
                            Lưu
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="grid gap-4">
                      <div>
                        <Label htmlFor="name" className="text-gray-700">Họ và tên</Label>
                        <Input
                          id="name"
                          value={profile.name}
                          onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                          disabled={!isEditing}
                          className="mt-1 border-pink-200 focus:border-pink-400"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-gray-700">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={profile.email}
                          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                          disabled={!isEditing}
                          className="mt-1 border-pink-200 focus:border-pink-400"
                        />
                      </div>

                      <div>
                        <Label htmlFor="dueDate" className="text-gray-700">Ngày dự sinh</Label>
                        <Input
                          id="dueDate"
                          type="date"
                          value={profile.dueDate}
                          onChange={(e) => setProfile({ ...profile, dueDate: e.target.value })}
                          disabled={!isEditing}
                          className="mt-1 border-pink-200 focus:border-pink-400"
                        />
                      </div>

                      <div>
                        <Label htmlFor="currentWeek" className="text-gray-700">Tuần thai hiện tại</Label>
                        <Input
                          id="currentWeek"
                          type="number"
                          value={profile.currentWeek}
                          onChange={(e) => setProfile({ ...profile, currentWeek: parseInt(e.target.value) })}
                          disabled={!isEditing}
                          className="mt-1 border-pink-200 focus:border-pink-400"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Notifications Tab */}
                  <TabsContent value="notifications" className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Thông báo gần đây</h3>
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-4 rounded-lg border transition-all hover:shadow-md ${
                          !notif.read ? 'bg-pink-50 border-pink-200' : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800 mb-1">{notif.title}</h4>
                            <p className="text-gray-600 text-sm mb-2">{notif.message}</p>
                            <p className="text-xs text-gray-500">{notif.time}</p>
                          </div>
                          {!notif.read && (
                            <Badge className="bg-pink-500 text-white">Mới</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </TabsContent>

                  {/* Settings Tab */}
                  <TabsContent value="settings" className="space-y-6">
                    {/* Facebook Messenger Settings */}
                    <MessengerSettings userId={currentUser?.id || 1} />
                    
                    {/* General Settings */}
                    <Card className="border-pink-100 shadow-lg">
                      <CardHeader>
                        <CardTitle className="text-gray-800">Cài đặt chung</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Card className="border-pink-100">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-800">Nhận thông báo email</h4>
                                <p className="text-sm text-gray-600">Nhận cập nhật qua email</p>
                              </div>
                              <input type="checkbox" className="w-5 h-5 text-pink-600" defaultChecked />
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="border-pink-100">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-800">Nhắc nhở hàng tuần</h4>
                                <p className="text-sm text-gray-600">Thông tin thai kỳ mỗi tuần</p>
                              </div>
                              <input type="checkbox" className="w-5 h-5 text-pink-600" defaultChecked />
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="border-pink-100">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-800">Thông báo lịch hẹn</h4>
                                <p className="text-sm text-gray-600">Nhắc nhở trước khám 1 ngày</p>
                              </div>
                              <input type="checkbox" className="w-5 h-5 text-pink-600" defaultChecked />
                            </div>
                          </CardContent>
                        </Card>

                        <Button
                          variant="outline"
                          className="w-full border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => toast.info('Tính năng đang phát triển')}
                        >
                          Đổi mật khẩu
                        </Button>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Profile;